import moment from 'moment-timezone';
import { FC } from 'react';
import { useEffect } from 'react';
import DateTime from 'react-datetime';
import styled from 'styled-components';
import { FormItem, ValidationState } from '../Form/FormItem';

const DateTimeInputElement = styled.div`
  .rdt {
    position: relative;
  }
  .rdtPicker {
    display: none;
    position: absolute;
    width: 250px;
    padding: 4px;
    margin-top: 1px;
    z-index: 99999 !important;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #f9f9f9;
  }
  .rdtOpen .rdtPicker {
    display: block;
  }
  .rdtStatic .rdtPicker {
    box-shadow: none;
    position: static;
  }

  .rdtPicker .rdtTimeToggle {
    text-align: center;
  }

  .rdtPicker table {
    width: 100%;
    margin: 0;
  }
  .rdtPicker td,
  .rdtPicker th {
    text-align: center;
    height: 28px;
  }
  .rdtPicker td {
    cursor: pointer;
  }
  .rdtPicker td.rdtDay:hover,
  .rdtPicker td.rdtHour:hover,
  .rdtPicker td.rdtMinute:hover,
  .rdtPicker td.rdtSecond:hover,
  .rdtPicker .rdtTimeToggle:hover {
    background: #eeeeee;
    cursor: pointer;
  }
  .rdtPicker td.rdtOld,
  .rdtPicker td.rdtNew {
    color: #999999;
  }
  .rdtPicker td.rdtToday {
    position: relative;
  }
  .rdtPicker td.rdtToday:before {
    content: '';
    display: inline-block;
    border-left: 7px solid transparent;
    border-bottom: 7px solid #428bca;
    border-top-color: rgba(0, 0, 0, 0.2);
    position: absolute;
    bottom: 4px;
    right: 4px;
  }
  .rdtPicker td.rdtActive,
  .rdtPicker td.rdtActive:hover {
    background-color: #428bca;
    color: #fff;
    text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.25);
  }
  .rdtPicker td.rdtActive.rdtToday:before {
    border-bottom-color: #fff;
  }
  .rdtPicker td.rdtDisabled,
  .rdtPicker td.rdtDisabled:hover {
    background: none;
    color: #999999;
    cursor: not-allowed;
  }

  .rdtPicker td span.rdtOld {
    color: #999999;
  }
  .rdtPicker td span.rdtDisabled,
  .rdtPicker td span.rdtDisabled:hover {
    background: none;
    color: #999999;
    cursor: not-allowed;
  }
  .rdtPicker th {
    border-bottom: 1px solid #f9f9f9;
  }
  .rdtPicker .dow {
    width: 14.2857%;
    border-bottom: none;
    cursor: default;
  }
  .rdtPicker th.rdtSwitch {
    width: 100px;
  }
  .rdtPicker th.rdtNext,
  .rdtPicker th.rdtPrev {
    font-size: 21px;
    vertical-align: top;
  }

  .rdtPrev span,
  .rdtNext span {
    display: block;
    user-select: none;
  }

  .rdtPicker th.rdtDisabled,
  .rdtPicker th.rdtDisabled:hover {
    background: none;
    color: #999999;
    cursor: not-allowed;
  }
  .rdtPicker thead tr:first-child th {
    cursor: pointer;
  }
  .rdtPicker thead tr:first-child th:hover {
    background: #eeeeee;
  }

  .rdtPicker tfoot {
    border-top: 1px solid #f9f9f9;
  }

  .rdtPicker button {
    border: none;
    background: none;
    cursor: pointer;
  }
  .rdtPicker button:hover {
    background-color: #eee;
  }

  .rdtPicker thead button {
    width: 100%;
    height: 100%;
  }

  td.rdtMonth,
  td.rdtYear {
    height: 50px;
    width: 25%;
    cursor: pointer;
  }
  td.rdtMonth:hover,
  td.rdtYear:hover {
    background: #eee;
  }

  .rdtCounters {
    display: inline-block;
  }

  .rdtCounters > div {
    float: left;
  }

  .rdtCounter {
    height: 100px;
    width: 40px;
  }

  .rdtCounterSeparator {
    line-height: 100px;
  }

  .rdtCounter .rdtBtn {
    height: 40%;
    line-height: 40px;
    cursor: pointer;
    display: block;
    user-select: none;
  }
  .rdtCounter .rdtBtn:hover {
    background: #eee;
  }
  .rdtCounter .rdtCount {
    height: 20%;
    font-size: 1.2em;
  }

  .rdtMilli {
    vertical-align: middle;
    padding-left: 8px;
    width: 48px;
  }

  .rdtTime td {
    cursor: default;
  }
  && input {
    border-radius: 0px;
    border: none;
    box-shadow: none;
    ${props => props.theme.partials.form.item.default};
    color: ${props => props.theme.vars.newColor.darkGS1};
    &::placeholder {
      color: ${props => props.theme.vars.newColor.darkGS3};
    }
    &:focus {
      border-color: transparent;
    }
  }
` as any;

export interface IProps {
  /** The label to display above the input box. */
  label?: string;
  /** Optional placeholder text. */
  placeholder?: string;
  /** Date value of the input specified as the number of ms since epoch */
  value: number;
  /** Timezone offset to show and parse date/time in - number of minutes */
  offset: number;
  /** Event fired when the value changes. */
  onChange: (value: number) => void;
  /** Input validation state (complete, warning)  */
  validationState?: ValidationState;
  /** Input validation message  */
  validationMessage?: string;
  /** Adds spacing below the component (default: true) */
  spacingBelow?: boolean;
  /** A hint can be passed as HTML to explain what the input is used for */
  hint?: any;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string;
}

const msInMinute = 60 * 1000;

/** @deprecated Do not use. Use `SingleDatePicker` and an `Input` with type set to time instead. */
export const DateTimeInput: FC<IProps> = props => {
  useEffect(() => {
    if (import.meta.env.VITE_ENV === 'poc') {
      // eslint-disable-next-line no-console
      console.warn(
        "'DateTimeInput' component has been deprecated. Please use `SingleDatePicker` and an `Input` with type set to time",
      );
    }
  }, []);

  const onChange = (time: moment.Moment | string) => {
    if (typeof time !== 'string') {
      props.onChange(time.utc().valueOf() - props.offset * msInMinute);
    }
  };

  const value = props.value ? new Date(props.value + props.offset * msInMinute) : null;

  return (
    <DateTimeInputElement data-testid="DateTimeInput">
      <FormItem
        label={props.label}
        validationMessage={props.validationMessage}
        validationState={props.validationState}
        spacingBelow={props.spacingBelow}
        hint={props.hint}
        tooltipHeading={props.tooltipHeading}
        tooltipDescription={props.tooltipDescription}
      >
        <DateTime
          value={value}
          onChange={onChange}
          inputProps={{ placeholder: props.placeholder }}
          utc={true}
          dateFormat="DD/MM/YYYY"
        />
      </FormItem>
    </DateTimeInputElement>
  );
};
